import { gaGetClientIdSync } from "utils/analytics"
import { ServiceType } from "utils/helpers"
import { FunnelStorage } from "modules/FunnelAnalytics"
import { BookingStatus, CreateTimeRequestResponse, ErrorResponse } from "utils/interfaces"
import { CURRENCY } from "utils/constants"
import { UserAccount } from "utils/UserAccount"
import { getTildaUtm } from "utils/utm"
import { httpClient } from "utils/infrastructure/httpClient"
import { getYmClientId } from "utils/analytics/yandex-metrika"

interface ClientData {
    phone: string
    email: string
    name: string
}

interface BaseData {
    client_data: Partial<ClientData>
    currency: CURRENCY
}

interface BookSessionRequestBody extends BaseData {
    service_type: ServiceType
    discount?: number
    date: string
    time: string
    slot_id: string
    created_by_psychologist: boolean
    psy_hash_id: string
}

interface Booking {
    id: string
    hash: string
    status: BookingStatus
    need_to_pay: boolean
}

interface BookingSessionResponse {
    bookings?: Booking[]
}

export function bookSessionUrl(onlyFree: boolean) {
    return onlyFree ? "booking/donation-session" : "book-session-client"
}

export function fetchCreateBookSession(data: BookSessionRequestBody, onlyFree: boolean) {
    if (!data.client_data.name) {
        data.client_data.name = UserAccount.name || ""
    }
    if (!data.client_data.phone) {
        data.client_data.phone = UserAccount.phone || ""
    }
    return httpClient
        .post<BookingSessionResponse & ErrorResponse>(bookSessionUrl(onlyFree), {
            ga_client_id: gaGetClientIdSync(),
            ym_client_id: getYmClientId(),
            utm: getTildaUtm(),
            source: FunnelStorage.getInstance().getBookingSource(),
            ...data,
        })
        .then((response) => {
            if (response.error) {
                throw new Error(response.error)
            }
            const booking = response?.bookings?.[0]
            if (!booking) {
                throw new Error("Booking not created")
            }
            return booking
        })
}

interface FetchCreateTimeRequest extends BaseData {
    psychologist: string
    discount: number | null
    service_type: number
    requested_session_datetimes: {
        date: string
        time: string
    }[]
}

export function fetchCreateTimeRequest(data: FetchCreateTimeRequest) {
    return httpClient
        .post<CreateTimeRequestResponse & ErrorResponse>("time-request/create", {
            ...data,
            ga_client_id: gaGetClientIdSync(),
            ym_client_id: getYmClientId(),
            utm: getTildaUtm(),
            source: FunnelStorage.getInstance().getBookingSource(),
        })
        .then((response) => {
            if (response.error) {
                throw new Error(response.error)
            }
            return response
        })
}

interface Deposit {
    id: number
    price: string
    price_usd: string
    price_raw: string
    price_raw_usd: string
    denomination: string
    created_at: string
    status: string
    google_calendar_event_id: string
    details: {
        psy_hash_id: string
        service_type_id: number
    }
    hash: string
    client: number
    discount: number | null
    configuration: number
    external_analytics: number[]
}

interface FetchCreateDeposit {
    id: number
    created_at: string
    client: number
    deposits: Deposit[]
    hash: string
    payment_system: string
}

export function fetchCreateDeposit(data: unknown) {
    return httpClient.post<FetchCreateDeposit & ErrorResponse>("pricing/deposit/book", data)
}
